import { CORPORATE_OFFERINGS, IMG } from "@/data/site";
import FinalCTA from "@/components/FinalCTA";
import { ButtonLink, Eyebrow, FadeIn, PageHero, usePageMeta } from "@/components/blocks";

const MODELS = [
  {
    n: "01",
    title: "Workshops & Sessions",
    copy: "Posture, mobility and desk-pain sessions delivered on-site or virtually — practical, not performative.",
  },
  {
    n: "02",
    title: "Ongoing Team Programs",
    copy: "Group fitness, wellness challenges and structured programs that run across weeks, not afternoons.",
  },
  {
    n: "03",
    title: "1:1 Executive Coaching",
    copy: "Individualized coaching for leadership teams operating under sustained high demand.",
  },
];

export default function Corporate() {
  usePageMeta(
    "Corporate Wellness Programs | StrengthLeap",
    "Corporate wellness built on a human performance system: desk-pain programs, mobility sessions, assessments, challenges and 1:1 coaching for teams."
  );

  return (
    <main>
      <PageHero
        testid="corporate-page"
        eyebrow="Corporate / B2B Ergonomics"
        lines={["Better people.", "Better workdays."]}
        copy="Modern work is hard on the human body. StrengthLeap corporate programs help teams reduce physical discomfort, move more, build healthier habits and improve everyday energy."
      />

      <section className="bg-bone px-6 py-28 text-ink sm:px-10 lg:px-16">
        <div className="mx-auto grid max-w-[1440px] gap-16 lg:grid-cols-[1fr_1.1fr] lg:gap-24">
          <div>
            <FadeIn>
              <Eyebrow tone="ink">What We Deliver</Eyebrow>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="mt-8 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl">
                Wellness that survives <span className="text-ink/40">Monday morning.</span>
              </h2>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-smoke sm:text-lg">
                Programs designed for real workplaces — measurable participation, credible coaching, and content your
                team will actually use.
              </p>
            </FadeIn>
            <FadeIn delay={0.3} className="mt-10">
              <ButtonLink to="/contact" variant="ink" testid="corporate-cta-contact">
                Talk to Our Corporate Team
              </ButtonLink>
            </FadeIn>
          </div>
          <div>
            {CORPORATE_OFFERINGS.map((o, i) => (
              <FadeIn key={o} delay={i * 0.05}>
                <p className="flex items-center justify-between border-t border-ink/15 py-4 text-sm text-ink/85 last:border-b sm:text-base">
                  {o}
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-ink">
        <div className="absolute inset-0">
          <img
            src={IMG.gymIndustrial}
            alt="Professional training environment"
            loading="lazy"
            className="h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/70 to-ink" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1440px] px-6 py-28 text-bone sm:px-10 lg:px-16">
          <FadeIn>
            <Eyebrow>Delivery Models</Eyebrow>
          </FadeIn>
          <div className="mt-12 grid gap-px border border-bone/20 bg-bone/20 lg:grid-cols-3">
            {MODELS.map((m, i) => (
              <FadeIn key={m.n} delay={i * 0.08} className="card-lift border border-white/5 bg-coal p-8 sm:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-lime">{m.n}</p>
                <h3 className="mt-5 font-display text-2xl font-black uppercase tracking-tight">{m.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-ash sm:text-base">{m.copy}</p>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <p className="mt-12 max-w-2xl text-base leading-relaxed text-ash sm:text-lg">
              Every engagement begins with an employee assessment and a program proposal scoped to your team size,
              goals and schedule.
            </p>
          </FadeIn>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
